import { useMemo, useState, ReactElement, useCallback } from 'react'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import cn from 'classnames'

import {
  PageLayout,
  Button,
  useGoogleMaps,
  mapMode as mapModes,
  safetyCaseHazardValuesToSafetyCaseHazardAddUpsert,
  showSuccessAlert,
  showErrorAlert,
  ISafetyCaseHazardValues,
} from '../../../components'
import SafetyCaseHazardBuilder from './safety-case-hazard-builder'

import { useCreateSafetyCaseHazardMutation, useSafetyCaseByIdQuery } from '../../../services'
import { useAppContext } from '../../../app-context'
import { claims, useAccessControl } from '../../../security'

import { IGeoJson } from '../../../interfaces'

const CreateSafetyCaseHazardPage = () => {
  const navigate = useNavigate()

  const { hasAccess } = useAccessControl()

  const hasSafetyCaseWriteAccess = useMemo(() => hasAccess([claims.safetyCase.write]), [hasAccess])

  const { dispatch: appDispatch } = useAppContext()

  const { safetyCaseId } = useParams()

  const { setMapMode } = useGoogleMaps()

  const [hazardGeoJson, setHazardGeoJson] = useState<IGeoJson | undefined>()

  const location = useLocation()

  const [headerControls, setHeaderControls] = useState<ReactElement | undefined>(undefined)

  const [backSearch] = useState((location?.state as any)?.backSearch)

  const _headerControls = useMemo(
    () => (
      <div>
        <Button
          onClick={() => navigate({ pathname: `/safety-cases/${safetyCaseId}/hazards`, search: backSearch })}
          className={cn(hasSafetyCaseWriteAccess && 'mr-2')}
          type="secondary"
        >
          Back To Hazards
        </Button>

        {hasSafetyCaseWriteAccess && headerControls}
      </div>
    ),
    [hasSafetyCaseWriteAccess, headerControls, navigate, backSearch, safetyCaseId],
  )

  const { mutateAsync: createHazard, isLoading: isHazardCreating } = useCreateSafetyCaseHazardMutation({})

  const { data: safetyCase } = useSafetyCaseByIdQuery(
    { id: Number(safetyCaseId) },
    { enabled: safetyCaseId !== undefined },
  )

  const defaultSortOrder = useMemo(() => {
    const existingSortOrders = (safetyCase?.hazards || [])
      .map(hazard => hazard.sortOrder)
      .filter(sortOrder => !isNaN(sortOrder))

    return existingSortOrders.length ? Math.max(...existingSortOrders) + 1 : 1
  }, [safetyCase?.hazards])

  const saveHazardInitialValues = useMemo(() => ({ sortOrder: String(defaultSortOrder) }), [defaultSortOrder])

  const [saveHazardForm, setSaveHazardForm] = useState<any>()

  const handleCreateHazard = useCallback(
    async (hazardValues: ISafetyCaseHazardValues) => {
      const hazardAddUpsert = safetyCaseHazardValuesToSafetyCaseHazardAddUpsert(hazardValues, {
        safetyCaseId: Number(safetyCaseId),
        geoJson: hazardGeoJson,
      })

      if (!hazardAddUpsert) {
        return
      }

      let createdHazard
      try {
        createdHazard = await createHazard(hazardAddUpsert)
      } catch (err) {
        appDispatch(showErrorAlert('Error creating hazard'))
        console.warn(err)
        return
      }

      setMapMode(mapModes.CLEAR)
      appDispatch(showSuccessAlert('Hazard successfully created'))

      if (createdHazard?.id) {
        navigate(
          {
            pathname: `/safety-cases/${safetyCaseId}/hazards/${createdHazard.id}/info`,
            search: backSearch,
          },
          { replace: true },
        )
      }
    },
    [appDispatch, createHazard, hazardGeoJson, navigate, safetyCaseId, setMapMode, backSearch],
  )

  const saveHeaderControls = useMemo(
    () => (
      <Button disabled={isHazardCreating} onClick={() => saveHazardForm?.submit()}>
        Create
      </Button>
    ),
    [isHazardCreating, saveHazardForm],
  )

  return (
    <PageLayout title="New Hazard" headerControls={_headerControls}>
      <SafetyCaseHazardBuilder
        hasWriteAccess={hasSafetyCaseWriteAccess}
        setHeaderControls={setHeaderControls}
        handleSaveHazard={handleCreateHazard}
        saveHeaderControls={saveHeaderControls}
        setHazardGeoJson={setHazardGeoJson}
        hazardGeoJson={hazardGeoJson}
        setSaveHazardForm={setSaveHazardForm}
        saveHazardInitialValues={saveHazardInitialValues}
        isSaveFormDisabled={isHazardCreating}
      />
    </PageLayout>
  )
}

export default CreateSafetyCaseHazardPage
